import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import AuthSplitCard from '../auth/AuthSplitCard'
import { Alert, Box, Button, CircularProgress, Link, Typography } from '@mui/material'
import { useAppDispatch, useAppSelector } from '../../hooks'
import { setAuthenticated, setUserData } from '../../stores/UserStore'
import authService from '../../services/authService'
import {
  acceptTerms,
  getConsentStatus,
  isMissingCloudFunction,
  isTermsVersionChangedError,
  resolveLegalLinks,
} from '../../consent/consentService'
import {
  MARKETING_EMAIL_WORDING_VERSION,
  TERMS_SCREEN_COPY,
  termsScreenBody,
} from '../../consent/consentCopy'
import type { ConsentStatus, TermsScreenVariant } from '../../consent/types'
import MarketingEmailCheckbox from './MarketingEmailCheckbox'

/** Past this, the user gets in as today rather than waiting on the consent status. */
export const TERMS_GATE_STATUS_TIMEOUT_MS = 5000

/**
 * The status usually answers well inside this, so the spinner waits for it rather than
 * flashing up for a frame on every sign-in. The overlay itself shows at once either way.
 */
export const TERMS_GATE_SPINNER_DELAY_MS = 300

/** Guests are `authenticated` with a fabricated `guest_<timestamp>` id (GuestJoinPage). */
export function isGuestUserId(id: string | null | undefined): boolean {
  return typeof id === 'string' && id.startsWith('guest_')
}

export type TermsGateOutcome = { show: false } | { show: true; status: ConsentStatus }

/**
 * Decide whether the Terms screen is due. Fails open on every failure — an old cloud without
 * the function, any error, or no answer within the timeout — because consent capture must
 * never lock anyone out of the app. A status that arrives after the timeout is ignored, so the
 * menu is not pulled away from someone already using it.
 */
export async function loadTermsGateOutcome(
  timeoutMs: number = TERMS_GATE_STATUS_TIMEOUT_MS
): Promise<TermsGateOutcome> {
  let timer: ReturnType<typeof setTimeout> | undefined
  const timeout = new Promise<'timeout'>((resolve) => {
    timer = setTimeout(() => resolve('timeout'), timeoutMs)
  })
  try {
    const result = await Promise.race([getConsentStatus(), timeout])
    if (result === 'timeout') {
      console.warn(`[TermsGate] No consent status after ${timeoutMs} ms; letting the user in.`)
      return { show: false }
    }
    // Without a published version there is nothing to accept, whatever the user flag says.
    if (result?.legal?.termsVersion && result.user?.needsTermsAcceptance === true) {
      return { show: true, status: result }
    }
    return { show: false }
  } catch (err) {
    if (isMissingCloudFunction(err)) {
      console.warn('[TermsGate] This cloud has no getConsentStatus yet; letting the user in.')
    } else {
      console.warn('[TermsGate] Could not load the consent status; letting the user in.', err)
    }
    return { show: false }
  } finally {
    if (timer) clearTimeout(timer)
  }
}

const overlaySx = {
  position: 'fixed',
  inset: 0,
  zIndex: 1300,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  bgcolor: 'var(--sevv-bg-default, #fafafa)',
  p: 3,
  overflowY: 'auto',
} as const

/**
 * The opaque overlay renders straight away so nothing behind it shows while the gate blocks;
 * only the spinner inside it is delayed.
 */
function TermsGateLoading() {
  const [showSpinner, setShowSpinner] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => setShowSpinner(true), TERMS_GATE_SPINNER_DELAY_MS)
    return () => clearTimeout(timer)
  }, [])

  return (
    <Box sx={overlaySx} role="status" aria-live="polite" data-testid="terms-gate-loading">
      {showSpinner && (
        <CircularProgress
          size={32}
          aria-label={TERMS_SCREEN_COPY.checking}
          sx={{ color: 'var(--sevv-primary, #f97415)' }}
        />
      )}
    </Box>
  )
}

interface TermsScreenProps {
  status: ConsentStatus
  onAccepted: () => void
  onVersionChanged: () => void
}

function TermsScreen({ status, onAccepted, onVersionChanged }: TermsScreenProps) {
  const dispatch = useAppDispatch()
  const platformUrl = useAppSelector((state) => state.workspace?.settings?.platform_url)
  // Starts in the recorded choice, so Continue never silently flips an earlier opt-in.
  const [marketingEmailOptIn, setMarketingEmailOptIn] = useState(
    status.user?.marketingEmailOptIn === true
  )
  const [busy, setBusy] = useState<'accept' | 'signOut' | null>(null)
  const [error, setError] = useState<string | null>(null)
  const dialogRef = useRef<HTMLDivElement>(null)

  // "welcome" reads correctly for anyone; "updated" would be wrong for a brand-new account.
  const variant: TermsScreenVariant = status.user?.variant === 'updated' ? 'updated' : 'welcome'
  const copy = TERMS_SCREEN_COPY[variant]
  const links = resolveLegalLinks(status.legal, platformUrl)

  useEffect(() => {
    dialogRef.current?.focus()
  }, [])

  const handleContinue = async () => {
    if (!status.legal) return
    setBusy('accept')
    setError(null)
    try {
      await acceptTerms({
        termsVersion: status.legal.termsVersion,
        marketingEmailOptIn,
        marketingEmailWordingVersion: MARKETING_EMAIL_WORDING_VERSION,
      })
      onAccepted()
    } catch (err) {
      // The policy was republished while the screen was open: show the new version instead
      // of recording agreement to one the user did not see.
      if (isTermsVersionChangedError(err)) {
        onVersionChanged()
        return
      }
      console.error('[TermsGate] acceptTerms failed:', err)
      setError(TERMS_SCREEN_COPY.acceptFailed)
      setBusy(null)
    }
  }

  // Same sequence as the MainMenu sign-out; the Redux clear runs even if signOut throws, so
  // the button always gets the user out.
  const handleSignOut = async () => {
    setBusy('signOut')
    try {
      await authService.signOut()
    } catch (err) {
      console.error('[TermsGate] Sign out failed; clearing the local session anyway:', err)
    } finally {
      dispatch(setUserData(null))
      dispatch(setAuthenticated(false))
    }
  }

  const linkSx = {
    color: 'var(--sevv-primary, #f97415)',
    fontWeight: 500,
  }

  return (
    // SC-438: the Terms screen shares the sign-in dialog's split layout. A new account is
    // welcomed in; an existing one sees the sign-in panel.
    <AuthSplitCard screen={variant === 'welcome' ? 'invite' : 'signIn'}>
      <Box
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="terms-gate-title"
        aria-describedby="terms-gate-body"
        tabIndex={-1}
        data-testid="terms-gate-screen"
        sx={{
          width: '100%',
          my: 'auto',
          color: 'var(--sevv-text-primary, #424242)',
          outline: 'none',
        }}
      >
        <Typography
          id="terms-gate-title"
          variant="h5"
          component="h1"
          sx={{ fontWeight: 700, mb: 1.5, color: 'var(--sevv-text-primary, #424242)' }}
        >
          {copy.title}
        </Typography>
        <Typography
          id="terms-gate-body"
          variant="body2"
          sx={{ mb: 2, color: 'var(--sevv-text-secondary, #757575)', lineHeight: 1.6 }}
        >
          {termsScreenBody(variant, status.legal?.effectiveDate)}
        </Typography>

        <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, mb: 2 }}>
          <Box component="li" sx={{ mb: 0.5 }}>
            <Link
              href={links.termsUrl}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
              sx={linkSx}
            >
              {TERMS_SCREEN_COPY.readTerms}
            </Link>
          </Box>
          <Box component="li">
            <Link
              href={links.privacyUrl}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
              sx={linkSx}
            >
              {TERMS_SCREEN_COPY.readPrivacy}
            </Link>
          </Box>
        </Box>

        <MarketingEmailCheckbox
          id="terms-gate-marketing-email"
          checked={marketingEmailOptIn}
          onChange={setMarketingEmailOptIn}
          disabled={busy !== null}
          sx={{ mb: 1 }}
        />

        <Typography
          variant="body2"
          data-testid="terms-gate-notice"
          sx={{ mb: 2, color: 'var(--sevv-text-secondary, #757575)' }}
        >
          {copy.notice}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            onClick={() => void handleSignOut()}
            disabled={busy !== null}
            data-testid="terms-gate-sign-out"
            sx={{
              color: 'var(--sevv-text-primary, #424242)',
              borderColor: 'var(--sevv-divider, #e0e0e0)',
            }}
          >
            {TERMS_SCREEN_COPY.signOut}
          </Button>
          <Button
            variant="contained"
            onClick={() => void handleContinue()}
            disabled={busy !== null}
            data-testid="terms-gate-continue"
            sx={{
              bgcolor: 'var(--sevv-primary, #f97415)',
              color: 'var(--sevv-white, #ffffff)',
              '&:hover': { bgcolor: 'var(--sevv-primary-dark, #ea580c)' },
            }}
          >
            {busy === 'accept' ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              TERMS_SCREEN_COPY.continue
            )}
          </Button>
        </Box>
      </Box>
    </AuthSplitCard>
  )
}

export interface TermsGateProviderProps {
  /** Signed in and not a guest. */
  enabled: boolean
  userId: string | null
  children?: React.ReactNode
}

/** `off`: signed out or a guest. `checking` and `required` hold back the menu. */
export type TermsGatePhase = 'off' | 'checking' | 'required' | 'clear'

interface TermsGateState {
  phase: TermsGatePhase
  /** Set while `phase` is `required`. */
  status: ConsentStatus | null
  onAccepted: () => void
  onVersionChanged: () => void
}

const noop = () => {}

// Without a provider (tests, or any tree that never mounts one) nothing is gated.
const TermsGateContext = createContext<TermsGateState>({
  phase: 'off',
  status: null,
  onAccepted: noop,
  onVersionChanged: noop,
})

type Resolved = { userId: string; outcome: TermsGateOutcome }

/**
 * Decides once, above the app shell, whether the signed-in user still has to accept the
 * current Terms (SC-360), and shares the answer with the parts it gates: AppController shows
 * `TermsGateView` in place of the menu, and `TermsGated` holds back the App-level siblings.
 *
 * It always renders its children unchanged. Swapping them for a loading or Terms element
 * would unmount AppController, whose remount re-runs the session check and the workspace
 * settings load and leaves a blank frame after the spinner.
 */
export const TermsGateProvider: React.FC<TermsGateProviderProps> = ({
  enabled,
  userId,
  children,
}) => {
  const [resolved, setResolved] = useState<Resolved | null>(null)
  const [reloadKey, setReloadKey] = useState(0)
  const active = enabled && !!userId

  useEffect(() => {
    if (!active || !userId) {
      // Every sign-in is checked afresh, even by the same user on the same tab: an earlier
      // fail-open or a Terms version published since must not let the menu through unchecked.
      setResolved(null)
      return
    }
    let cancelled = false
    void loadTermsGateOutcome().then((outcome) => {
      if (!cancelled) setResolved({ userId, outcome })
    })
    return () => {
      cancelled = true
    }
  }, [active, userId, reloadKey])

  const onAccepted = useCallback(() => {
    setResolved((current) => (current ? { ...current, outcome: { show: false } } : current))
  }, [])

  const onVersionChanged = useCallback(() => {
    setResolved(null)
    setReloadKey((k) => k + 1)
  }, [])

  // Keyed by user so a different account signing in is checked without a frame of the
  // previous user's answer.
  const current = active && resolved?.userId === userId ? resolved.outcome : null
  let phase: TermsGatePhase = 'off'
  if (active) phase = !current ? 'checking' : current.show ? 'required' : 'clear'
  const status = current?.show ? current.status : null

  const value = useMemo<TermsGateState>(
    () => ({ phase, status, onAccepted, onVersionChanged }),
    [phase, status, onAccepted, onVersionChanged]
  )

  return <TermsGateContext.Provider value={value}>{children}</TermsGateContext.Provider>
}

/** True while the menu and everything that can start a room must stay unmounted. */
export function useTermsGateBlocking(): boolean {
  const { phase } = useContext(TermsGateContext)
  return phase === 'checking' || phase === 'required'
}

/**
 * What renders in place of the menu while the gate blocks: a loading state while checking,
 * then the Terms screen. Nothing otherwise.
 */
export const TermsGateView: React.FC = () => {
  const { phase, status, onAccepted, onVersionChanged } = useContext(TermsGateContext)
  if (phase === 'checking') return <TermsGateLoading />
  if (phase !== 'required' || !status) return null
  return (
    <TermsScreen
      key={status.legal?.termsVersion}
      status={status}
      onAccepted={onAccepted}
      onVersionChanged={onVersionChanged}
    />
  )
}

/**
 * Renders its children only when the gate does not block: the App-level siblings of the menu
 * (profile check, feedback, product updates) wait with it, so nothing but the Terms screen
 * appears until the user is through. The URL is never touched, so a rid/room deep link resumes
 * in MainMenu after Continue.
 */
export const TermsGated: React.FC<{ children?: React.ReactNode }> = ({ children }) =>
  useTermsGateBlocking() ? null : <>{children}</>

export default TermsGateProvider
